import path from "node:path";
import * as p from "@clack/prompts";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import { Command } from "commander";
import { logErr, logInfo, logOk, S } from "../helpers.ts";
import { openCatalogSource } from "../open-library.ts";
import { syncProjectSources } from "./source.ts";

/**
 * Creates the `skaile update` command.
 *
 * Two stages:
 *   1. **Catalog cache refresh** — invalidates the {@link RemoteCatalogSource}
 *      cache at `~/.skaile/cache/catalog/` and re-fetches the asset list. This
 *      is the Phase 2 entry point for users in air-gapped mode
 *      (`catalog.cache_ttl: 0`) or anyone who wants a fresh view of the public
 *      skaile.store catalog before installing.
 *   2. **Asset re-deploy** — `AssetManager.install()`
 *      re-deploys any outdated assets in the current project from updated
 *      repositories. This is the Phase 1 behavior; preserved here.
 *
 * Pass `--no-catalog` to skip stage 1 (asset-only); pass `--catalog-only` to
 * skip stage 2 (catalog-only). The default runs both.
 *
 * Before re-deploy, `update` offers to **sync sources** from upstream (default
 * yes). Re-deploy resolves against the local source cache, which is frozen at
 * the commit captured when each source was first cloned — without a sync,
 * `update` can never see newer GitHub commits and always reports "Nothing to
 * update". Pass `--no-sync` to skip, `-y`/`--yes` to sync without the prompt; a
 * non-interactive shell defaults to syncing.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#update
 */
export function makeUpdateCommand(): Command {
  return new Command("update")
    .description("Refresh the Catalog cache and re-deploy outdated assets in the current project")
    .argument("[name]", "Update a specific asset (kind:name)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--no-catalog", "Skip refreshing the catalog cache")
    .option("--catalog-only", "Refresh the catalog cache only; do not re-deploy assets")
    .option("--no-sync", "Skip syncing sources from upstream before re-deploying")
    .option("-y, --yes", "Sync sources without the confirmation prompt")
    .action(
      async (
        _name: string | undefined,
        opts: {
          projectDir: string;
          catalog: boolean;
          catalogOnly?: boolean;
          sync: boolean;
          yes?: boolean;
        },
      ) => {
        // Stage 1 — catalog cache refresh (Phase 2)
        if (opts.catalog !== false) {
          try {
            const catalogSpinner = p.spinner();
            catalogSpinner.start("Refreshing catalog cache");
            const remote = await openCatalogSource({ projectDir: path.resolve(opts.projectDir) });
            const result = await remote.refresh();
            catalogSpinner.stop(
              `Catalog refreshed: ${S.heading(String(result.assetsCached))} assets cached`,
            );
            logInfo(`Source: ${remote.baseUrl}`);
          } catch (err) {
            logErr(`Catalog refresh failed: ${err instanceof Error ? err.message : String(err)}`);
            logInfo("Continuing with asset re-deploy. Run `skaile catalog test` to debug.");
          }
        }

        if (opts.catalogOnly) return;

        const projectDir = path.resolve(opts.projectDir);

        // Stage 2a — source sync (opt-out, default yes). Re-deploy reads the
        // local source cache; without a pull it can't see upstream commits.
        if (await shouldSyncSources(opts)) {
          const syncSpinner = p.spinner();
          syncSpinner.start("Syncing sources");
          const { total, synced, failed } = await syncProjectSources(projectDir);
          syncSpinner.stop(
            total === 0
              ? "No sources to sync."
              : `Synced ${S.heading(`${synced}/${total}`)} source(s)` +
                  (failed ? ` (${failed} failed)` : ""),
          );
        }

        // Stage 2b — asset re-deploy against the (now-synced) source cache.
        const am = new AssetManager({ projectDir });
        const spinner = p.spinner();
        spinner.start("Re-deploying assets");
        const result = await am.install();
        spinner.stop("Done");
        if (result.deployed.length > 0) {
          for (const d of result.deployed) logOk(`Updated ${d}`);
        } else {
          logInfo("Nothing to update.");
        }
      },
    );
}

/**
 * Decide whether to sync sources before re-deploy. `--no-sync` (`opts.sync ===
 * false`) always wins; `-y/--yes` and non-interactive shells sync without
 * asking; otherwise prompt with yes as the default. A cancelled prompt (Esc /
 * Ctrl-C) aborts the whole command via {@link process.exit}.
 */
async function shouldSyncSources(opts: { sync: boolean; yes?: boolean }): Promise<boolean> {
  if (opts.sync === false) return false;
  if (opts.yes || !process.stdin.isTTY) return true;
  const answer = await p.confirm({
    message: "Sync sources from upstream before re-deploying?",
    initialValue: true,
  });
  if (p.isCancel(answer)) {
    p.cancel("Cancelled.");
    process.exit(130);
  }
  return answer;
}
